import { useState, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { useAuth } from "../contexts/AuthContext";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Progress } from "../components/ui/progress";
import { toast } from "sonner";
import axios from "axios";
import {
  ChevronRight,
  ChevronLeft,
  Upload,
  Check,
  Loader2,
  Camera,
  ShoppingBag,
  RefreshCw,
  Plus,
  Trash2,
  Crown,
} from "lucide-react";
import { AltalabLogo } from "../components/AltalabLogo";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Platform options
const PLATFORMS = [
  { id: "telegram", name: "Telegram", types: ["vip", "free"] },
  { id: "privacy", name: "Privacy", types: ["vip", "pago", "free"] },
  { id: "onlyfans", name: "OnlyFans", types: ["vip", "free"] },
  { id: "instagram", name: "Instagram", types: ["profile"] },
  { id: "twitter", name: "Twitter/X", types: ["profile"] },
  { id: "tiktok", name: "TikTok", types: ["profile"] },
  { id: "closefans", name: "CloseFans", types: ["vip", "free"] },
];

const PIX_TYPES = [
  { id: "cpf", name: "CPF" },
  { id: "email", name: "E-mail" },
  { id: "phone", name: "Telefone" },
  { id: "random", name: "Chave Aleatória" },
];

const OnboardingPage = () => {
  const navigate = useNavigate();
  const { token, refreshUser, user } = useAuth();
  const [currentStep, setCurrentStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const fileInputRef = useRef(null);

  // Step 1: Interests
  const [interests, setInterests] = useState({
    sell: false,
    exchange: false,
  });

  // Step 2: Platforms (for sellers)
  const [platforms, setPlatforms] = useState([]);

  // Step 3: PIX info (for sellers)
  const [pixInfo, setPixInfo] = useState({
    type: "cpf",
    key: "",
    holderName: "",
  });

  // Step 4: First package (optional)
  const [firstPackage, setFirstPackage] = useState({
    selectedPlatforms: [],
    title: "",
    includes: [],
    price: "",
    deliveryTime: "24h",
  });

  // Step 5: Profile photo
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [photoPreview, setPhotoPreview] = useState(null);

  // Calculate total steps based on interests
  const getTotalSteps = () => {
    if (interests.sell) {
      return 5; // Interest -> Platforms -> PIX -> Package -> Photo
    }
    return 3; // Interest -> Platforms (for exchanges) -> Photo
  };

  const totalSteps = getTotalSteps();
  const progress = (currentStep / totalSteps) * 100;

  // Add platform
  const addPlatform = () => {
    setPlatforms([
      ...platforms,
      { platform: "", type: "vip", link: "", reach: "" },
    ]);
  };

  // Remove platform
  const removePlatform = (index) => {
    setPlatforms(platforms.filter((_, i) => i !== index));
  };

  // Update platform
  const updatePlatform = (index, field, value) => {
    const updated = [...platforms];
    updated[index][field] = value;
    setPlatforms(updated);
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Preview
    const reader = new FileReader();
    reader.onload = (e) => setPhotoPreview(e.target.result);
    reader.readAsDataURL(file);

    // Upload
    const formData = new FormData();
    formData.append("file", file);

    try {
      const response = await axios.post(
        `${API}/upload/photo?Authorization=Bearer ${token}`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      setProfilePhoto(response.data.url);
      toast.success("Foto enviada!");
    } catch (error) {
      toast.error("Erro ao enviar foto");
    }
  };

  // Navigation
  const canGoNext = () => {
    switch (currentStep) {
      case 1:
        return interests.sell || interests.exchange;
      case 2:
        return platforms.length > 0 && platforms.every((p) => p.platform && p.reach);
      case 3:
        if (interests.sell) {
          return pixInfo.key && pixInfo.holderName;
        }
        return true; // Photo step for exchange-only users
      case 4:
        return true; // Package is optional
      case 5:
        return true; // Photo is optional
      default:
        return true;
    }
  };

  const handleNext = async () => {
    if (!canGoNext()) {
      toast.error("Preencha os campos obrigatórios");
      return;
    }

    setLoading(true);
    try {
      // Save data to backend based on step
      if (currentStep === 1) {
        // Save interests
        await axios.put(`${API}/onboarding/interests?Authorization=Bearer ${token}`, {
          interests: Object.keys(interests).filter((k) => interests[k]),
        });
      } else if (currentStep === 2) {
        // Save platforms
        await axios.put(`${API}/onboarding/platforms?Authorization=Bearer ${token}`, {
          platforms: platforms,
        });
      } else if (currentStep === 3 && interests.sell) {
        // Save PIX info
        await axios.put(`${API}/onboarding/pix?Authorization=Bearer ${token}`, {
          pix_type: pixInfo.type,
          pix_key: pixInfo.key,
          pix_holder_name: pixInfo.holderName,
        });
      } else if (currentStep === 4 && interests.sell && firstPackage.selectedPlatforms.length > 0 && firstPackage.title && firstPackage.price) {
        // Create first service package (multi-platform)
        const reachParts = firstPackage.selectedPlatforms.map((pid) => {
          const p = platforms.find((pl) => pl.platform === pid);
          return p ? `${p.reach}k` : "";
        }).filter(Boolean);
        await axios.post(`${API}/services?Authorization=Bearer ${token}`, {
          platforms: firstPackage.selectedPlatforms,
          service_type: "vip",
          title: firstPackage.title,
          description: firstPackage.includes.join(", "),
          includes: firstPackage.includes,
          price: parseFloat(firstPackage.price),
          reach: reachParts.join(" + "),
          delivery_time: firstPackage.deliveryTime,
        });
      }

      if (currentStep < totalSteps) {
        setCurrentStep(currentStep + 1);
      } else {
        // Complete onboarding
        await axios.put(`${API}/onboarding/complete?Authorization=Bearer ${token}`);
        await refreshUser();
        toast.success("Cadastro completo! Bem-vindo ao altalab!");
        navigate("/home");
      }
    } catch (error) {
      console.error("Onboarding error:", error);
      toast.error(error.response?.data?.detail || "Erro ao salvar");
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
    }
  };

  // Skip to end
  const handleSkip = async () => {
    setLoading(true);
    try {
      await axios.put(`${API}/onboarding/complete?Authorization=Bearer ${token}`);
      await refreshUser();
      toast.success("Cadastro completo!");
      navigate("/home");
    } catch (error) {
      toast.error("Erro ao finalizar");
    } finally {
      setLoading(false);
    }
  };

  // Render step content
  const renderStep = () => {
    switch (currentStep) {
      case 1:
        return renderInterestsStep();
      case 2:
        return renderPlatformsStep();
      case 3:
        return interests.sell ? renderPixStep() : renderPhotoStep();
      case 4:
        return interests.sell ? renderPackageStep() : null;
      case 5:
        return renderPhotoStep();
      default:
        return null;
    }
  };

  // Step 1: Interests
  const renderInterestsStep = () => (
    <motion.div
      key="interests"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-8">
        <h1 className="text-2xl font-bold mb-2">
          Bem-vindo ao altalab, {user?.display_name?.split(" ")[0] || "Criador"}! 🎉
        </h1>
        <p className="text-white/50">O que você gostaria de fazer aqui?</p>
        <p className="text-white/30 text-sm mt-1">(Pode selecionar as duas opções)</p>
      </div>

      <div className="space-y-4">
        {/* Sell option */}
        <button
          onClick={() => setInterests({ ...interests, sell: !interests.sell })}
          className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
            interests.sell
              ? "border-violet-500 bg-violet-500/10"
              : "border-white/10 bg-white/[0.02] hover:border-white/20"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${interests.sell ? "bg-violet-500" : "bg-white/10"}`}>
              <ShoppingBag className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Quero Vender Divulgação</h3>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  interests.sell ? "border-violet-500 bg-violet-500" : "border-white/30"
                }`}>
                  {interests.sell && <Check className="w-4 h-4" />}
                </div>
              </div>
              <p className="text-white/50 mt-1">
                Monetize sua audiência vendendo posts, stories e menções
              </p>
            </div>
          </div>
        </button>

        {/* Exchange option */}
        <button
          onClick={() => setInterests({ ...interests, exchange: !interests.exchange })}
          className={`w-full p-6 rounded-2xl border-2 transition-all text-left ${
            interests.exchange
              ? "border-emerald-500 bg-emerald-500/10"
              : "border-white/10 bg-white/[0.02] hover:border-white/20"
          }`}
        >
          <div className="flex items-start gap-4">
            <div className={`p-3 rounded-xl ${interests.exchange ? "bg-emerald-500" : "bg-white/10"}`}>
              <RefreshCw className="w-6 h-6" />
            </div>
            <div className="flex-1">
              <div className="flex items-center justify-between">
                <h3 className="font-semibold text-lg">Quero Fazer Trocas Gratuitas</h3>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center ${
                  interests.exchange ? "border-emerald-500 bg-emerald-500" : "border-white/30"
                }`}>
                  {interests.exchange && <Check className="w-4 h-4" />}
                </div>
              </div>
              <p className="text-white/50 mt-1">
                Troque divulgação com outros criadores sem pagar nada
              </p>
              <span className="inline-block mt-2 px-2 py-0.5 bg-emerald-500/20 text-emerald-400 text-xs rounded-full">
                100% GRÁTIS
              </span>
            </div>
          </div>
        </button>
      </div>
    </motion.div>
  );

  // Step 2: Platforms
  const renderPlatformsStep = () => (
    <motion.div
      key="platforms"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-2">Suas Plataformas</h1>
        <p className="text-white/50">
          {interests.sell
            ? "Cadastre as plataformas onde você pode divulgar"
            : "Cadastre suas plataformas para fazer trocas"}
        </p>
      </div>

      <div className="space-y-4">
        {platforms.map((platform, index) => (
          <div
            key={index}
            className="p-4 bg-white/[0.02] border border-white/10 rounded-xl space-y-4"
          >
            <div className="flex items-center justify-between">
              <span className="text-sm text-white/50">Plataforma {index + 1}</span>
              <button
                onClick={() => removePlatform(index)}
                className="text-red-400 hover:text-red-300 p-1"
              >
                <Trash2 className="w-4 h-4" />
              </button>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 text-sm">Plataforma</Label>
                <select
                  value={platform.platform}
                  onChange={(e) => updatePlatform(index, "platform", e.target.value)}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                >
                  <option value="" className="bg-[#1a1a1a]">Selecione</option>
                  {PLATFORMS.map((p) => (
                    <option key={p.id} value={p.id} className="bg-[#1a1a1a]">
                      {p.name}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <Label className="text-white/70 text-sm">Tipo</Label>
                <select
                  value={platform.type}
                  onChange={(e) => updatePlatform(index, "type", e.target.value)}
                  className="w-full mt-1 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
                >
                  <option value="vip" className="bg-[#1a1a1a]">VIP</option>
                  <option value="pago" className="bg-[#1a1a1a]">Pago</option>
                  <option value="free" className="bg-[#1a1a1a]">Free</option>
                </select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label className="text-white/70 text-sm">Link ou @</Label>
                <Input
                  value={platform.link}
                  onChange={(e) => updatePlatform(index, "link", e.target.value)}
                  placeholder="@seuusuario"
                  className="bg-white/5 border-white/10"
                />
              </div>

              <div>
                <Label className="text-white/70 text-sm">Seguidores/Assinantes</Label>
                <div className="flex items-center gap-2 mt-1">
                  <Input
                    value={platform.reach}
                    onChange={(e) => updatePlatform(index, "reach", e.target.value)}
                    placeholder="15"
                    className="bg-white/5 border-white/10"
                  />
                  <span className="text-white/50">k</span>
                </div>
              </div>
            </div>
          </div>
        ))}

        <button
          onClick={addPlatform}
          className="w-full p-4 border-2 border-dashed border-white/20 rounded-xl text-white/50 hover:text-white hover:border-white/40 transition-colors flex items-center justify-center gap-2"
        >
          <Plus className="w-5 h-5" />
          Adicionar Plataforma
        </button>
      </div>
    </motion.div>
  );

  // Step 3: PIX Info (for sellers)
  const renderPixStep = () => (
    <motion.div
      key="pix"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-2">💳 Dados para Recebimento</h1>
        <p className="text-white/50">
          Configure sua chave PIX para receber pagamentos
        </p>
      </div>

      <div className="p-6 bg-white/[0.02] border border-white/10 rounded-xl space-y-6">
        <div>
          <Label className="text-white/70">Tipo de Chave PIX</Label>
          <div className="grid grid-cols-2 gap-2 mt-2">
            {PIX_TYPES.map((type) => (
              <button
                key={type.id}
                onClick={() => setPixInfo({ ...pixInfo, type: type.id })}
                className={`p-3 rounded-lg border transition-all ${
                  pixInfo.type === type.id
                    ? "border-[#FD5564] bg-[#FD5564]/10"
                    : "border-white/10 bg-white/5 hover:border-white/20"
                }`}
              >
                {type.name}
              </button>
            ))}
          </div>
        </div>

        <div>
          <Label className="text-white/70">Chave PIX</Label>
          <Input
            value={pixInfo.key}
            onChange={(e) => setPixInfo({ ...pixInfo, key: e.target.value })}
            placeholder={
              pixInfo.type === "cpf"
                ? "000.000.000-00"
                : pixInfo.type === "email"
                ? "seu@email.com"
                : pixInfo.type === "phone"
                ? "(00) 00000-0000"
                : "Chave aleatória"
            }
            className="mt-2 bg-white/5 border-white/10"
          />
        </div>

        <div>
          <Label className="text-white/70">Nome do Titular (como aparece no PIX)</Label>
          <Input
            value={pixInfo.holderName}
            onChange={(e) => setPixInfo({ ...pixInfo, holderName: e.target.value })}
            placeholder="Nome Completo"
            className="mt-2 bg-white/5 border-white/10"
          />
        </div>

        <p className="text-sm text-white/40 flex items-start gap-2">
          <span className="text-yellow-500">⚠️</span>
          Esta informação será mostrada para compradores no momento do pagamento.
        </p>
      </div>
    </motion.div>
  );

  // Step 4: First Package (for sellers)
  const renderPackageStep = () => {
    const packageIncludes = [
      "Post no feed",
      "Post fixado",
      "Story/Status",
      "Menção na bio",
      "Mensagem em massa",
      "Link direto",
    ];

    const togglePlatformSelection = (platformId) => {
      const current = firstPackage.selectedPlatforms;
      const updated = current.includes(platformId)
        ? current.filter((id) => id !== platformId)
        : [...current, platformId];
      setFirstPackage({ ...firstPackage, selectedPlatforms: updated });
    };

    return (
      <motion.div
        key="package"
        initial={{ opacity: 0, x: 20 }}
        animate={{ opacity: 1, x: 0 }}
        exit={{ opacity: 0, x: -20 }}
        className="space-y-6"
      >
        <div className="text-center mb-6">
          <h1 className="text-2xl font-bold mb-2">Crie seu Primeiro Pacote</h1>
          <p className="text-white/50">
            Selecione as plataformas e o que está incluso
          </p>
          <p className="text-white/30 text-sm mt-1">(Você pode pular e criar depois)</p>
        </div>

        <div className="p-6 bg-white/[0.02] border border-white/10 rounded-xl space-y-6">
          <div>
            <Label className="text-white/70">Plataformas incluídas neste pacote</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {platforms.map((p, i) => {
                const isSelected = firstPackage.selectedPlatforms.includes(p.platform);
                const platformName = PLATFORMS.find((pl) => pl.id === p.platform)?.name || p.platform;
                return (
                  <button
                    key={i}
                    onClick={() => togglePlatformSelection(p.platform)}
                    className={`p-3 rounded-lg border text-sm text-left transition-all flex items-center gap-2 ${
                      isSelected
                        ? "border-[#FD5564] bg-[#FD5564]/10 text-white"
                        : "border-white/10 bg-white/5 text-white/60 hover:border-white/20"
                    }`}
                    data-testid={`pkg-platform-${p.platform}`}
                  >
                    <div className={`w-5 h-5 rounded border-2 flex items-center justify-center flex-shrink-0 ${
                      isSelected ? "border-[#FD5564] bg-[#FD5564]" : "border-white/30"
                    }`}>
                      {isSelected && <Check className="w-3 h-3" />}
                    </div>
                    <div>
                      <div className="font-medium">{platformName}</div>
                      <div className="text-xs text-white/40">{p.reach}k {p.type}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-white/70">Título do Pacote</Label>
            <Input
              value={firstPackage.title}
              onChange={(e) => setFirstPackage({ ...firstPackage, title: e.target.value })}
              placeholder="Ex: Combo Privacy + Telegram: 3 Posts + Fixado"
              className="mt-2 bg-white/5 border-white/10"
            />
          </div>

          <div>
            <Label className="text-white/70">O que está incluso?</Label>
            <div className="grid grid-cols-2 gap-2 mt-2">
              {packageIncludes.map((item) => (
                <button
                  key={item}
                  onClick={() => {
                    const includes = firstPackage.includes.includes(item)
                      ? firstPackage.includes.filter((i) => i !== item)
                      : [...firstPackage.includes, item];
                    setFirstPackage({ ...firstPackage, includes });
                  }}
                  className={`p-2 rounded-lg border text-sm transition-all ${
                    firstPackage.includes.includes(item)
                      ? "border-[#FD5564] bg-[#FD5564]/10 text-white"
                      : "border-white/10 bg-white/5 text-white/60 hover:border-white/20"
                  }`}
                >
                  {firstPackage.includes.includes(item) && <Check className="w-3 h-3 inline mr-1" />}
                  {item}
                </button>
              ))}
            </div>
          </div>

          <div className="grid grid-cols-2 gap-4">
            <div>
              <Label className="text-white/70">Preço (R$)</Label>
              <Input
                type="number"
                value={firstPackage.price}
                onChange={(e) => setFirstPackage({ ...firstPackage, price: e.target.value })}
                placeholder="150"
                className="mt-2 bg-white/5 border-white/10"
              />
            </div>

            <div>
              <Label className="text-white/70">Prazo de Entrega</Label>
              <select
                value={firstPackage.deliveryTime}
                onChange={(e) => setFirstPackage({ ...firstPackage, deliveryTime: e.target.value })}
                className="w-full mt-2 bg-white/5 border border-white/10 rounded-lg px-3 py-2 text-white"
              >
                <option value="12h" className="bg-[#1a1a1a]">12 horas</option>
                <option value="24h" className="bg-[#1a1a1a]">24 horas</option>
                <option value="48h" className="bg-[#1a1a1a]">48 horas</option>
                <option value="72h" className="bg-[#1a1a1a]">72 horas</option>
              </select>
            </div>
          </div>
        </div>
      </motion.div>
    );
  };

  // Step 5: Profile Photo
  const renderPhotoStep = () => (
    <motion.div
      key="photo"
      initial={{ opacity: 0, x: 20 }}
      animate={{ opacity: 1, x: 0 }}
      exit={{ opacity: 0, x: -20 }}
      className="space-y-6"
    >
      <div className="text-center mb-6">
        <h1 className="text-2xl font-bold mb-2">📷 Foto de Perfil</h1>
        <p className="text-white/50">
          Adicione uma foto para aumentar a confiança do seu perfil
        </p>
      </div>

      <div className="flex flex-col items-center">
        <div
          onClick={() => fileInputRef.current?.click()}
          className="w-40 h-40 rounded-full bg-white/[0.02] border-2 border-dashed border-white/20 flex items-center justify-center cursor-pointer hover:border-[#FD5564]/50 transition-colors overflow-hidden"
        >
          {photoPreview ? (
            <img src={photoPreview} alt="Preview" className="w-full h-full object-cover" />
          ) : (
            <div className="text-center">
              <Camera className="w-10 h-10 text-white/30 mx-auto mb-2" />
              <span className="text-sm text-white/50">Clique para enviar</span>
            </div>
          )}
        </div>
        <input
          ref={fileInputRef}
          type="file"
          accept="image/*"
          onChange={handlePhotoUpload}
          className="hidden"
        />

        <p className="text-sm text-white/40 mt-4 text-center">
          Recomendamos uma foto de rosto clara e bem iluminada
        </p>
      </div>

      {/* Summary */}
      <div className="mt-8 p-6 bg-white/[0.02] border border-white/10 rounded-xl">
        <h3 className="font-semibold mb-4">✅ Resumo do seu perfil</h3>
        <div className="space-y-2 text-sm">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-emerald-400" />
            <span className="text-white/70">
              Interesses: {interests.sell && "Vender"} {interests.sell && interests.exchange && "e"} {interests.exchange && "Trocar"}
            </span>
          </div>
          {platforms.length > 0 && (
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-white/70">
                {platforms.length} plataforma{platforms.length > 1 ? "s" : ""} cadastrada{platforms.length > 1 ? "s" : ""}
              </span>
            </div>
          )}
          {interests.sell && pixInfo.key && (
            <div className="flex items-center gap-2">
              <Check className="w-4 h-4 text-emerald-400" />
              <span className="text-white/70">PIX configurado para recebimento</span>
            </div>
          )}
        </div>
      </div>
    </motion.div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col">
      {/* Header */}
      <header className="p-4 flex items-center justify-between border-b border-white/5">
        <AltalabLogo size="small" />
        <span className="text-sm text-white/50">
          Passo {currentStep} de {totalSteps}
        </span>
      </header>

      {/* Progress */}
      <Progress value={progress} className="h-1 bg-white/10" />

      {/* Content */}
      <main className="flex-1 flex items-center justify-center p-4">
        <div className="w-full max-w-lg">
          <AnimatePresence mode="wait">{renderStep()}</AnimatePresence>
        </div>
      </main>

      {/* Footer */}
      <footer className="p-4 border-t border-white/5">
        <div className="max-w-lg mx-auto flex items-center justify-between">
          <Button
            variant="ghost"
            onClick={handleBack}
            disabled={currentStep === 1}
            className="text-white/50 hover:text-white"
          >
            <ChevronLeft className="w-4 h-4 mr-1" />
            Voltar
          </Button>

          <div className="flex gap-2">
            {currentStep >= 3 && (
              <Button
                variant="ghost"
                onClick={handleSkip}
                disabled={loading}
                className="text-white/50 hover:text-white"
              >
                Pular
              </Button>
            )}
            <Button
              onClick={handleNext}
              disabled={!canGoNext() || loading}
              className="bg-gradient-to-r from-[#FD5564] to-[#FF7854] hover:opacity-90"
            >
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : currentStep === totalSteps ? (
                <>
                  Finalizar
                  <Check className="w-4 h-4 ml-1" />
                </>
              ) : (
                <>
                  Próximo
                  <ChevronRight className="w-4 h-4 ml-1" />
                </>
              )}
            </Button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default OnboardingPage;
